import { inject, Injectable, Signal, Type } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  modalController = inject(ModalController);

  private openModals = new Map<string, HTMLIonModalElement>();

  async open<R, C = any>(component: Type<C>, options?: ModalOptions<C>) {
    const backdropOnly = options?.backdropOnly ? 'backdrop-modal transparent-background' : '';
    const componentProps = Object.assign({}, options?.componentProps, { modalId: options?.id });
    const modal = await this.modalController.create({
      component,
      canDismiss: options?.canDismiss,
      componentProps: componentProps as Partial<C>, // Safely cast here
      backdropDismiss: true,
      breakpoints: options?.swipeable ? [0, 1] : undefined,
      initialBreakpoint: options?.swipeable ? 1 : undefined,
      handle: options?.handle ?? false,
      cssClass: `centered-modal ${backdropOnly}`,
      id: options?.id,
    });
    if (options?.id) {
      this.openModals.set(options.id, modal);
    }
    await modal.present();
    return modal.onWillDismiss<R>();
  }

  close(id: string, data?: any) {
    const modalToClose = this.openModals.get(id);
    if (!modalToClose) return;
    return modalToClose.dismiss(data);
  }
}

export interface ModalOptions<C> {
  canDismiss?: boolean | ((data?: any, role?: string) => Promise<boolean>);
  componentProps?: Partial<MappedComponent<C>>;
  backdropOnly?: boolean;
  handle?: boolean;
  id?: string;
  swipeable?: boolean; // whether the user can swipe it down to dismiss
}

// Since ionic creates signals for us, we need to extract the signal parameter type here.
type MappedComponent<C> = {
  [K in keyof C]: C[K] extends Signal<infer T> ? T : C[K] extends (...args: any) => any ? Parameters<C[K]> : C[K];
} & { id?: string; };